// src/services/errorHandlingService.ts
import { reportError, reportMessage } from "@/utils/sentry";
import { notificationService } from "@/services/notificationService";
import type { AxiosError } from "axios";

// 错误类型
export type ErrorType =
  | "network_error"
  | "api_error"
  | "wechat_sdk_error"
  | "wechat_auth_error"
  | "wechat_api_error"
  | "compatibility_error"
  | "permission_error"
  | "validation_error"
  | "timeout_error"
  | "signature_error"
  | "config_error"
  | "unknown_error";

// 严重级别
export type ErrorSeverity = "low" | "medium" | "high" | "critical";

// 错误信息
export interface ErrorInfo {
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  details?: unknown;
  timestamp: number;
  userAgent?: string;
  url?: string;
  userId?: string;
  sessionId?: string;
  retryCount?: number;
  maxRetries?: number;
}

// 重试配置
export interface RetryConfig {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffFactor: number;
  retryCondition?: (error: unknown) => boolean;
  onRetry?: (error: unknown, retryCount: number) => void;
}

// 配置
export interface ErrorHandlingConfig {
  // 是否显示用户友好的错误提示
  showUserMessage: boolean;
  // 是否自动上报错误
  autoReport: boolean;
  // 是否记录错误日志
  logErrors: boolean;
  // 默认重试配置
  defaultRetryConfig: RetryConfig;
  // 特定错误类型的重试配置
  errorTypeRetryConfigs: Partial<Record<ErrorType, RetryConfig>>;
}

// 默认配置
const DEFAULT_CONFIG: ErrorHandlingConfig = {
  showUserMessage: true,
  autoReport: true,
  logErrors: true,
  defaultRetryConfig: {
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 10000,
    backoffFactor: 2,
  },
  errorTypeRetryConfigs: {
    network_error: {
      maxRetries: 5,
      initialDelay: 1000,
      maxDelay: 30000,
      backoffFactor: 2,
      retryCondition: (error) =>
        !error.response || error.response.status >= 500,
    },
    wechat_sdk_error: {
      maxRetries: 3,
      initialDelay: 2000,
      maxDelay: 8000,
      backoffFactor: 2,
    },
    signature_error: {
      maxRetries: 2,
      initialDelay: 5000,
      maxDelay: 10000,
      backoffFactor: 1.5,
    },
    timeout_error: {
      maxRetries: 4,
      initialDelay: 2000,
      maxDelay: 16000,
      backoffFactor: 2,
    },
  },
};

export class ErrorHandlingService {
  private config: ErrorHandlingConfig;
  private errorHistory: ErrorInfo[] = [];
  private readonly maxHistorySize = 100;

  constructor(config?: Partial<ErrorHandlingConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /** 处理错误 */
  public async handleError(
    error: unknown,
    type?: ErrorType,
    context?: Record<string, unknown>,
  ): Promise<void> {
    const errorInfo = this.analyzeError(error, type, context);

    // 记录错误历史
    this.addToErrorHistory(errorInfo);

    // 记录日志
    if (this.config.logErrors) {
      this.logError(errorInfo);
    }

    // 自动上报错误
    if (this.config.autoReport) {
      this.reportError(errorInfo);
    }

    // 显示用户友好的错误提示
    if (this.config.showUserMessage) {
      this.showUserMessage(errorInfo);
    }
  }

  /** 带重试的异步执行 */
  public async executeWithRetry<T>(
    operation: () => Promise<T>,
    errorType: ErrorType = "unknown_error",
    customRetryConfig?: Partial<RetryConfig>,
  ): Promise<T> {
    const retryConfig = this.getRetryConfig(errorType, customRetryConfig);
    let lastError: unknown;

    for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;

        // 检查是否应该重试
        if (attempt >= retryConfig.maxRetries) {
          break;
        }

        // 检查重试条件
        if (retryConfig.retryCondition && !retryConfig.retryCondition(error)) {
          break;
        }

        // 计算延迟时间
        const delay = this.calculateDelay(attempt, retryConfig);

        // 调用重试回调
        if (retryConfig.onRetry) {
          retryConfig.onRetry(error, attempt + 1);
        }

        // 记录重试信息
        reportMessage(
          `操作重试 ${attempt + 1}/${retryConfig.maxRetries}`,
          "warning",
          {
            action: "operation_retry",
            errorType,
            attempt: attempt + 1,
            maxRetries: retryConfig.maxRetries,
            delay,
            error: this.getErrorSummary(error),
          },
        );

        // 等待延迟
        await this.delay(delay);
      }
    }

    // 所有重试都失败了，处理最终错误
    await this.handleError(lastError, errorType, {
      finalAttempt: true,
      totalAttempts: retryConfig.maxRetries + 1,
    });

    throw lastError;
  }

  /** 获取错误历史 */
  public getErrorHistory(): ErrorInfo[] {
    return [...this.errorHistory];
  }

  /** 清空错误历史 */
  public clearErrorHistory(): void {
    this.errorHistory = [];
  }

  /** 获取错误统计 */
  public getErrorStats() {
    const total = this.errorHistory.length;
    const last24h = this.errorHistory.filter(
      (error) => Date.now() - error.timestamp < 24 * 60 * 60 * 1000,
    ).length;

    const byType = this.errorHistory.reduce(
      (acc, error) => {
        acc[error.type] = (acc[error.type] || 0) + 1;
        return acc;
      },
      {} as Record<ErrorType, number>,
    );

    const bySeverity = this.errorHistory.reduce(
      (acc, error) => {
        acc[error.severity] = (acc[error.severity] || 0) + 1;
        return acc;
      },
      {} as Record<ErrorSeverity, number>,
    );

    return {
      total,
      last24h,
      byType,
      bySeverity,
      latestError: this.errorHistory[this.errorHistory.length - 1],
    };
  }

  /** 分析错误 */
  private analyzeError(
    error: unknown,
    type?: ErrorType,
    context?: Record<string, unknown>,
  ): ErrorInfo {
    let errorType = type || this.detectErrorType(error);
    let severity = this.determineSeverity(error, errorType);
    const message = this.extractErrorMessage(error);

    // 特殊处理不同类型的错误
    if (error.isAxiosError || error.response) {
      errorType = "api_error";
      const axiosError = error as AxiosError;

      if (!axiosError.response) {
        errorType = "network_error";
        severity = "high";
      } else {
        const status = axiosError.response.status;
        if (status >= 500) {
          severity = "high";
        } else if (status === 401 || status === 403) {
          errorType = "permission_error";
          severity = "medium";
        } else if (status === 400) {
          errorType = "validation_error";
          severity = "low";
        }
      }
    }

    return {
      type: errorType,
      severity,
      message,
      details: {
        originalError: error,
        stack: error.stack,
        context,
        ...this.extractErrorDetails(error),
      },
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
    };
  }

  /** 检测错误类型 */
  private detectErrorType(error: unknown): ErrorType {
    if (error.isAxiosError) {
      return error.response ? "api_error" : "network_error";
    }

    if (error.name === "TimeoutError" || error.code === "TIMEOUT") {
      return "timeout_error";
    }

    if (error.message?.includes("wx") || error.message?.includes("微信")) {
      return "wechat_sdk_error";
    }

    if (
      error.message?.includes("permission") ||
      error.message?.includes("权限")
    ) {
      return "permission_error";
    }

    if (
      error.message?.includes("signature") ||
      error.message?.includes("签名")
    ) {
      return "signature_error";
    }

    return "unknown_error";
  }

  /** 确定严重级别 */
  private determineSeverity(error: unknown, type: ErrorType): ErrorSeverity {
    // 根据错误类型确定严重级别
    const severityMap: Record<ErrorType, ErrorSeverity> = {
      network_error: "high",
      api_error: "medium",
      wechat_sdk_error: "medium",
      wechat_auth_error: "medium",
      wechat_api_error: "low",
      compatibility_error: "medium",
      permission_error: "high",
      validation_error: "low",
      timeout_error: "medium",
      signature_error: "high",
      config_error: "critical",
      unknown_error: "medium",
    };

    let severity = severityMap[type] || "medium";

    // 根据错误内容调整严重级别
    if (
      error.message?.includes("critical") ||
      error.message?.includes("严重")
    ) {
      severity = "critical";
    } else if (
      error.message?.includes("fatal") ||
      error.message?.includes("致命")
    ) {
      severity = "critical";
    }

    return severity;
  }

  /** 提取错误消息 */
  private extractErrorMessage(error: unknown): string {
    if (typeof error === "string") {
      return error;
    }

    if (error.message) {
      return error.message;
    }

    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    if (error.response?.statusText) {
      return error.response.statusText;
    }

    return "未知错误";
  }

  /** 提取错误详情 */
  private extractErrorDetails(error: unknown): Record<string, unknown> {
    const details: Record<string, unknown> = {};

    if (error.isAxiosError) {
      details.url = error.config?.url;
      details.method = error.config?.method;
      details.status = error.response?.status;
      details.statusText = error.response?.statusText;
      details.responseData = error.response?.data;
    }

    if (error.code) {
      details.code = error.code;
    }

    if (error.name) {
      details.name = error.name;
    }

    return details;
  }

  /** 获取重试配置 */
  private getRetryConfig(
    errorType: ErrorType,
    customConfig?: Partial<RetryConfig>,
  ): RetryConfig {
    const typeConfig = this.config.errorTypeRetryConfigs[errorType] || {};
    return {
      ...this.config.defaultRetryConfig,
      ...typeConfig,
      ...customConfig,
    };
  }

  /** 计算延迟 */
  private calculateDelay(attempt: number, config: RetryConfig): number {
    const exponentialDelay =
      config.initialDelay * Math.pow(config.backoffFactor, attempt);
    const jitteredDelay = exponentialDelay * (0.5 + Math.random() * 0.5); // 添加抖动
    return Math.min(jitteredDelay, config.maxDelay);
  }

  /** 延迟 */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** 记录到错误历史 */
  private addToErrorHistory(errorInfo: ErrorInfo): void {
    this.errorHistory.push(errorInfo);

    // 限制历史记录大小
    if (this.errorHistory.length > this.maxHistorySize) {
      this.errorHistory.shift();
    }
  }

  /** 记录错误日志 */
  private logError(errorInfo: ErrorInfo): void {
    const logLevel =
      errorInfo.severity === "critical"
        ? "error"
        : errorInfo.severity === "high"
          ? "error"
          : errorInfo.severity === "medium"
            ? "warn"
            : "info";

    console[logLevel]("Error handled:", {
      type: errorInfo.type,
      severity: errorInfo.severity,
      message: errorInfo.message,
      timestamp: new Date(errorInfo.timestamp).toISOString(),
      details: errorInfo.details,
    });
  }

  /** 上报错误 */
  private reportError(errorInfo: ErrorInfo): void {
    reportError(new Error(errorInfo.message), {
      type: errorInfo.type,
      severity: errorInfo.severity,
      timestamp: errorInfo.timestamp,
      url: errorInfo.url,
      userId: errorInfo.userId,
      sessionId: errorInfo.sessionId,
      details: errorInfo.details,
    });
  }

  /** 显示用户提示 */
  private showUserMessage(errorInfo: ErrorInfo): void {
    const userMessageKey = this.getUserMessageKey(errorInfo.type);

    // 根据严重级别选择不同的提示方式
    const duration = this.getMessageDuration(errorInfo.severity);

    switch (errorInfo.severity) {
      case "critical":
        notificationService.error(userMessageKey, { duration });
        break;
      case "high":
        notificationService.error(userMessageKey, { duration });
        break;
      case "medium":
        notificationService.warning(userMessageKey, { duration });
        break;
      case "low":
        notificationService.info(userMessageKey, { duration });
        break;
    }
  }

  /** 获取错误类型对应的 i18n 键 */
  private getUserMessageKey(errorType: ErrorType): string {
    const messageKeyMap: Record<ErrorType, string> = {
      network_error: "error.networkError",
      api_error: "error.apiError",
      wechat_sdk_error: "error.wechatSdkError",
      wechat_auth_error: "error.wechatAuthError",
      wechat_api_error: "error.wechatApiError",
      compatibility_error: "error.compatibilityError",
      permission_error: "error.permissionError",
      validation_error: "error.validationError",
      timeout_error: "error.timeoutError",
      signature_error: "error.signatureError",
      config_error: "error.configError",
      unknown_error: "error.unknownError",
    };

    return messageKeyMap[errorType] || "error.unknownError";
  }

  /** 获取消息显示时长（毫秒） */
  private getMessageDuration(severity: ErrorSeverity): number {
    const durationMap: Record<ErrorSeverity, number> = {
      critical: 10000, // 10秒
      high: 6000, // 6秒
      medium: 4000, // 4秒
      low: 3000, // 3秒
    };

    return durationMap[severity] || 4000;
  }

  /** 获取当前用户 ID */
  private getCurrentUserId(): string | undefined {
    // 可以从 JWT token 或其他地方获取用户 ID
    try {
      // 这里可以集成实际的用户认证系统
      return undefined;
    } catch {
      return undefined;
    }
  }

  /** 获取会话 ID */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem("session_id");
    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem("session_id", sessionId);
    }
    return sessionId;
  }

  /** 生成会话 ID */
  private generateSessionId(): string {
    return (
      "sess_" +
      Date.now().toString(36) +
      "_" +
      Math.random().toString(36).substring(2)
    );
  }

  /** 获取错误摘要 */
  private getErrorSummary(error: unknown): Record<string, unknown> {
    return {
      message: this.extractErrorMessage(error),
      type: error.constructor.name,
      code: error.code,
      status: error.response?.status,
    };
  }

  /** 更新配置 */
  public updateConfig(newConfig: Partial<ErrorHandlingConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /** 创建特定类型错误处理器 */
  public createErrorHandler(
    errorType: ErrorType,

    _customConfig?: Partial<RetryConfig>,
  ) {
    return async (error: unknown, context?: Record<string, unknown>) => {
      await this.handleError(error, errorType, context);
    };
  }

  /** 创建带重试的操作包装器 */
  public createRetryWrapper<TArgs extends unknown[], TReturn>(
    fn: (...args: TArgs) => Promise<TReturn>,
    errorType: ErrorType = "unknown_error",
    retryConfig?: Partial<RetryConfig>,
  ) {
    return async (...args: TArgs): Promise<TReturn> => {
      return this.executeWithRetry(() => fn(...args), errorType, retryConfig);
    };
  }
}

// 全局错误处理服务实例
export const errorHandlingService = new ErrorHandlingService();
